/* istanbul ignore file */
import { FormSection, FormRow, BigFormRow, MediumFormRow } from 'framework/ui/layout';
import { useEffect, useState } from 'react';
import VacancyWysiwygInput from 'domain/recruiting/vacancy/shared/input/VacancyWysiwygInput';
import { EnumDropdown, useFormContext, TextInput, CountyOfResidencesEnumDropdown, COUNTRIES } from 'framework/form';
import { useVacancyGroupId } from 'domain/recruiting/newVacancy/routing';
import useVacancyPublicationService, {
  PUBLICATION_PLATFORM,
  PUBLICATION_STATUS,
} from 'domain/recruiting/newVacancy/show/publications/vacancyPublicationService';
import MinimalAndBetterEnglishLevelEnumDropdown from 'domain/recruiting/newVacancy/show/publications/shared/input/MinimalAndBetterEnglishLevelEnumDropdown';
import { OfficeDropdown } from 'domain/shared/employee';
import { EMPLOYEE_CONTRACT_TYPES } from 'domain/shared/newVacancy';
import { RELOCATION_OPTIONS } from 'domain/recruiting/vacancy/shared/vacancyService';
import AdditionalVacancyQuestionList from './AdditionalVacancyQuestionList';
import AdditionalVacancyQuestion from './AdditionalVacancyQuestion';
import { Loading } from 'framework/ui/mui-builder';

interface VacancyPublicationFormProps {
  isCreateForm?: boolean;
}

const COUNTRY_OF_RESIDENCE = COUNTRIES.filter(country => country.name !== 'Unknown').reduce(
  (prev, curr) => ({ ...prev, [curr.name.toUpperCase()]: curr.name }),
  {},
);

const ALL_DROP_DOWN_COUNTRIES = {
  ALL: 'All',
  EU: 'EU',
  ...COUNTRY_OF_RESIDENCE,
};

export const ENGLISH_LEVEL_OPTIONS = {
  BEGINNER: 'Beginner',
  ELEMENTARY: 'Elementary',
  INTERMEDIATE: 'Intermediate',
  'UPPER INTERMEDIATE': 'Upper Intermediate',
  ADVANCED: 'Advanced',
  PROFICIENCY: 'Proficiency',
};

const VacancyPublicationForm = ({ isCreateForm = false }: VacancyPublicationFormProps) => {
  const { onChangeCallback } = useFormContext();
  const vacancyId = useVacancyGroupId();
  const { constructVacancyPublicationDescription } = useVacancyPublicationService();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (onChangeCallback && vacancyId && isCreateForm) {
        const vacancyDescription = await constructVacancyPublicationDescription(vacancyId);
        onChangeCallback({ ...vacancyDescription }, true);
        setLoading(false);
      } else {
        setLoading(false);
      }
    })();
  }, [vacancyId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <FormSection title="Vacancy publication">
        <FormRow>
          <EnumDropdown
            source="type"
            label="Publication platform"
            options={PUBLICATION_PLATFORM}
            placeholder="Choose platform"
          />
          <EnumDropdown source="status" label="Status" options={PUBLICATION_STATUS} placeholder="Choose Status" />
        </FormRow>
        <FormRow>
          <MinimalAndBetterEnglishLevelEnumDropdown
            source="englishLevels"
            label="Relevant english levels"
            options={ENGLISH_LEVEL_OPTIONS}
            multiple
            placeholder="Choose minimal relevant english level"
            helpText="Choose minimal relevant english level and higher levels will be selected automatically.
            To remove the level - start from the lowest"
          />
          <CountyOfResidencesEnumDropdown
            source="countryOfResidences"
            label="Relevant country of residence"
            options={ALL_DROP_DOWN_COUNTRIES}
            multiple
            placeholder="Choose relevant country"
          />
        </FormRow>
        <FormRow>
          <OfficeDropdown isAnywhereAvailable source="details.location" label="Location of registration" />
          <EnumDropdown
            source="details.contractType"
            label="Contract type"
            options={EMPLOYEE_CONTRACT_TYPES}
            multiple
            placeholder="Choose relevant contract type"
          />
          <EnumDropdown
            source="details.relocationOption"
            label="Relocation options"
            options={RELOCATION_OPTIONS}
            placeholder="Choose relevant relocation options"
          />
        </FormRow>
        <BigFormRow>
          <AdditionalVacancyQuestionList
            source="formFields"
            inputTemplate={AdditionalVacancyQuestion}
            addText="Add question"
          />
        </BigFormRow>
        {isCreateForm && loading ? (
          <Loading />
        ) : (
          <>
            <MediumFormRow>
              <TextInput source="name" />
            </MediumFormRow>
            <VacancyWysiwygInput source="description" label="Public description" />
          </>
        )}
      </FormSection>
    </>
  );
};

export default VacancyPublicationForm;
